/**
 * Autotiling and map compilation (TRD-02 R-02-12c, TRD-12 §5).
 *
 * Authors write a path polyline plus blocker rectangles. This turns that into the
 * `buildMask` — the single runtime authority on buildability (R-02-12) — and the two tile
 * layers.
 *
 * Doing it in code rather than by hand matters for two reasons. Hand-writing an 18×32
 * mask that agrees with a polyline is tedious and silently wrong when it doesn't. And
 * generated daily maps (TRD-13 §4.4) have to be compiled by *this same function*, so any
 * divergence between authored and generated maps would be a divergence in the compiler,
 * where one test catches it, rather than in 3 hand-typed files where nothing does.
 */
import { MAP_HEIGHT, MAP_WIDTH } from './schemas/common.js';
import type { MapDef, MapSource, PathDef } from './schemas/defs.js';

export const TILE = {
  VOID: 0,
  GROUND_FIRST: 1,
  GROUND_VARIANTS: 4,
  PATH_FIRST: 5,
  BLOCKED_FIRST: 21,
  DECOR_FIRST: 37,
  SPAWN_FIRST: 45,
  CORE_FIRST: 49,
} as const;

export type MaskChar = '.' | '#' | 'p' | 's' | 'c';

/** Sampling step when rasterizing a polyline. Fine enough that no tile is skipped. */
const WALK_STEP = 0.2;

function idx(x: number, y: number): number {
  return y * MAP_WIDTH + x;
}

function inBounds(x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x < MAP_WIDTH && y < MAP_HEIGHT;
}

/** Deterministic ground variant from position — no RNG, so compilation is reproducible. */
function groundVariant(x: number, y: number): number {
  let h = (x * 73856093) ^ (y * 19349663);
  h = (h ^ (h >>> 13)) >>> 0;
  return h % TILE.GROUND_VARIANTS;
}

/** Every tile the polyline passes through, in order. */
export function rasterizePath(path: PathDef): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = [];
  const seen = new Set<number>();

  for (let i = 0; i < path.waypoints.length - 1; i++) {
    const a = path.waypoints[i];
    const b = path.waypoints[i + 1];
    if (!a || !b) continue;

    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    const steps = Math.max(1, Math.ceil(length / WALK_STEP));

    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const x = Math.floor(a.x + dx * t);
      const y = Math.floor(a.y + dy * t);
      if (!inBounds(x, y)) continue;
      const key = idx(x, y);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ x, y });
    }
  }
  return out;
}

/** Total polyline length in tiles — the single most important balance lever (TRD-13 R-13-08). */
export function pathLength(path: PathDef): number {
  let total = 0;
  for (let i = 0; i < path.waypoints.length - 1; i++) {
    const a = path.waypoints[i];
    const b = path.waypoints[i + 1];
    if (!a || !b) continue;
    total += Math.sqrt((b.x - a.x) ** 2 + (b.y - a.y) ** 2);
  }
  return total;
}

/** 4-bit N/E/S/W mask, bit set when that neighbour is the SAME material (TRD-12 A-12c). */
function blobIndex(
  mask: MaskChar[][],
  x: number,
  y: number,
  matches: (c: MaskChar) => boolean,
): number {
  const same = (nx: number, ny: number): number => {
    if (!inBounds(nx, ny)) return 0;
    const row = mask[ny];
    if (!row) return 0;
    const c = row[nx];
    return c !== undefined && matches(c) ? 1 : 0;
  };
  return same(x, y - 1) * 1 + same(x + 1, y) * 2 + same(x, y + 1) * 4 + same(x - 1, y) * 8;
}

export function compileMap(source: MapSource): MapDef {
  const mask: MaskChar[][] = Array.from({ length: MAP_HEIGHT }, () =>
    Array.from({ length: MAP_WIDTH }, (): MaskChar => '.'),
  );

  const set = (x: number, y: number, c: MaskChar): void => {
    if (!inBounds(x, y)) return;
    const row = mask[y];
    if (row) row[x] = c;
  };

  // Blockers first, so a path crossing one still wins — the path must always be walkable.
  for (const b of source.blockers) {
    for (let y = Math.min(b.y0, b.y1); y <= Math.max(b.y0, b.y1); y++) {
      for (let x = Math.min(b.x0, b.x1); x <= Math.max(b.x0, b.x1); x++) {
        set(x, y, '#');
      }
    }
  }

  for (const path of source.paths) {
    for (const tile of rasterizePath(path)) set(tile.x, tile.y, 'p');
  }

  const spawns = source.paths
    .map((p) => p.waypoints[0])
    .filter((w): w is { x: number; y: number } => w !== undefined);

  for (const s of spawns) set(Math.floor(s.x), Math.floor(s.y), 's');
  set(Math.floor(source.core.x), Math.floor(source.core.y), 'c');

  // ── Autotile ──
  const groundLayer = new Array<number>(MAP_WIDTH * MAP_HEIGHT).fill(TILE.VOID);
  const overlayLayer = new Array<number>(MAP_WIDTH * MAP_HEIGHT).fill(TILE.VOID);

  const isPathLike = (c: MaskChar): boolean => c === 'p' || c === 's' || c === 'c';

  for (let y = 0; y < MAP_HEIGHT; y++) {
    for (let x = 0; x < MAP_WIDTH; x++) {
      const c = mask[y]?.[x] ?? '.';
      const i = idx(x, y);

      if (isPathLike(c)) {
        groundLayer[i] = TILE.PATH_FIRST + blobIndex(mask, x, y, isPathLike);
      } else if (c === '#') {
        groundLayer[i] = TILE.BLOCKED_FIRST + blobIndex(mask, x, y, (n) => n === '#');
      } else {
        groundLayer[i] = TILE.GROUND_FIRST + groundVariant(x, y);
      }

      // Spawn portal and Core are the only hand-drawn environment art (TRD-12 A-12e), and
      // they sit in the overlay so they draw over the path beneath them.
      if (c === 's') overlayLayer[i] = TILE.SPAWN_FIRST;
      if (c === 'c') overlayLayer[i] = TILE.CORE_FIRST;
    }
  }

  return {
    id: source.id,
    name: source.name,
    biome: source.biome,
    width: MAP_WIDTH,
    height: MAP_HEIGHT,
    paths: source.paths,
    core: source.core,
    spawns,
    buildMask: mask.map((row) => row.join('')),
    groundLayer,
    overlayLayer,
    ...(source.modifier !== undefined ? { modifier: source.modifier } : {}),
    generated: false,
  };
}

/** Buildable tile count — V-9 requires ≥ 200, TRD-13 V-G2 requires ≥ 180. */
export function countBuildable(map: MapDef): number {
  let n = 0;
  for (const row of map.buildMask) {
    for (const c of row) if (c === '.') n++;
  }
  return n;
}

/**
 * Minimum buildable tiles within `radius` of any path tile — the coverage floor.
 *
 * TRD-13 V-G3 leans on this: a stretch of path with nothing to build beside it is a
 * stretch the player simply cannot defend, and it is the most common way a naive
 * generator emits an unwinnable map. Authored maps are checked with the same function so
 * the two sources are held to one standard.
 */
export function minPathCoverage(map: MapDef, radius = 3): number {
  let worst = Number.POSITIVE_INFINITY;

  for (let y = 0; y < MAP_HEIGHT; y++) {
    for (let x = 0; x < MAP_WIDTH; x++) {
      if (map.buildMask[y]?.[x] !== 'p') continue;

      let buildable = 0;
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          const nx = x + dx;
          const ny = y + dy;
          if (!inBounds(nx, ny)) continue;
          if (dx * dx + dy * dy > radius * radius) continue;
          if (map.buildMask[ny]?.[nx] === '.') buildable++;
        }
      }
      if (buildable < worst) worst = buildable;
    }
  }

  return worst === Number.POSITIVE_INFINITY ? 0 : worst;
}
